import type { QuestionDefinition } from "@reno-estimator/core";

/**
 * Exterior's full question set (Phase 13B) - see rules/exterior.ts for
 * the matching calculator and its module doc comment for the full "why"
 * behind this shape. Per the brief's explicit "do not assume every
 * exterior project is a complete exterior renovation" / "allow users to
 * select relevant scope": every category below is its own independent
 * single_select question, defaulting to "none" - the same
 * independent-gate shape the common questions already use for
 * electrical/plumbing/demolition, rather than one multi-select
 * "which categories apply" checklist question feeding a single combined
 * scope. Each question can be answered (or left as "none"/unanswered)
 * completely independently of every other - selecting siding does not
 * imply anything about windows, roofing, or any other category.
 */
export const exteriorQuestions: QuestionDefinition[] = [
  {
    id: "exterior_siding_scope",
    projectTypes: ["exterior"],
    inputType: "single_select",
    prompt: "Is siding work part of this project?",
    required: false,
    options: [
      { id: "none", label: "No siding work" },
      { id: "partial_repair", label: "Partial repair", description: "Repairing or replacing siding on a portion of the home." },
      { id: "full_replacement", label: "Full replacement", description: "Replacing all siding on the home." }
    ],
    order: 180
  },
  {
    id: "exterior_windows_scope",
    projectTypes: ["exterior"],
    inputType: "single_select",
    prompt: "Is exterior window replacement part of this project?",
    required: false,
    options: [
      { id: "none", label: "No window replacement" },
      { id: "some_replaced", label: "Some windows replaced" },
      { id: "full_replacement", label: "All windows replaced" }
    ],
    order: 190
  },
  {
    id: "exterior_doors_scope",
    projectTypes: ["exterior"],
    inputType: "single_select",
    prompt: "Is exterior entry door replacement part of this project?",
    helpText: "Exterior entry doors only (garage doors are asked separately under a garage project).",
    required: false,
    options: [
      { id: "none", label: "No door replacement" },
      { id: "one_door", label: "One door" },
      { id: "multiple_doors", label: "Multiple doors" }
    ],
    order: 200
  },
  {
    id: "exterior_roofing_work_scope",
    projectTypes: ["exterior"],
    inputType: "single_select",
    prompt: "Is any roofing work part of this project?",
    helpText: "This covers minor repair or flashing/edge work tied into your exterior project - NOT a full roof replacement, which is out of scope for this estimator (get a dedicated roofing quote for that).",
    required: false,
    options: [
      { id: "none", label: "No roofing work" },
      { id: "minor_repair_flashing", label: "Minor repair / flashing" },
      { id: "tied_to_siding_work", label: "Roofing work tied to siding job", description: "E.g. re-flashing or edge work needed while siding is being replaced." }
    ],
    order: 210
  },
  {
    id: "exterior_trim_fascia_soffit_scope",
    projectTypes: ["exterior"],
    inputType: "single_select",
    prompt: "Is trim, fascia, or soffit work part of this project?",
    required: false,
    options: [
      { id: "none", label: "No trim/fascia/soffit work" },
      { id: "basic", label: "Basic" },
      { id: "premium", label: "Premium" }
    ],
    order: 220
  },
  {
    id: "exterior_finishes_paint_scope",
    projectTypes: ["exterior"],
    inputType: "single_select",
    prompt: "Is exterior painting or refinishing part of this project?",
    required: false,
    options: [
      { id: "none", label: "No painting/refinishing" },
      { id: "spot_touch_up", label: "Spot touch-up" },
      { id: "full_repaint", label: "Full repaint/refinish" }
    ],
    order: 230
  },
  {
    id: "exterior_structural_repairs",
    projectTypes: ["exterior"],
    inputType: "boolean",
    prompt: "Are any exterior structural repairs known to be needed (e.g. rot, water damage, settling)?",
    helpText: "This can only be a rough allowance - actual scope needs to be confirmed once the affected area is opened up.",
    required: false,
    order: 240
  },
  {
    id: "exterior_design_compliance_scope",
    projectTypes: ["exterior"],
    inputType: "single_select",
    prompt: "Do you need architectural/design services, or heritage-district/HOA compliance review?",
    helpText: "Distinct from the general design-services question - this covers exterior-specific design work and any heritage-district or condo/HOA approval process.",
    required: false,
    options: [
      { id: "none", label: "Neither" },
      { id: "design_services", label: "Architectural/design services" },
      { id: "heritage_hoa_compliance", label: "Heritage-district or HOA/condo-board compliance review" }
    ],
    order: 250
  }
];
